'use client';

import { Box, Button, Typography } from '@mui/material';
import { useState } from 'react';
import type { StructuredContentDocument } from '@repo/supergraph-schemas/structuredContent/types';
import { BrandIcon, type BrandIconType } from '../../../BrandIcon';
import { theme } from '../../../NerdWalletThemeProvider';

export type SMBInfoGatheringCtaProps = StructuredContentDocument<{
  headline: string;
  answers: {
    answer: string;
    destination?: string;
  }[];
  ctaText?: string;
  icon: BrandIconType;
  subText?: string;
}>;

export function SMBInfoGatheringCta({ props }: SMBInfoGatheringCtaProps) {
  const { headline, answers = [], ctaText = 'learn more', icon, subText } = props;
  const [selectedAnswer, setSelectedAnswer] = useState(answers?.[0]);
  const encodedArrow = `data:image/svg+xml;utf8,${encodeURIComponent(`<svg width="17" height="10" viewBox="0 0 17 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.49937 9.6262L0.960938 2.0878L2.19937 0.849365L8.49937 7.14937L14.7994 0.849365L16.0378 2.0878L8.49937 9.6262Z"
      fill="#64666A"
    />
  </svg>`)}`;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        boxShadow: 1,
        marginY: 4,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          padding: { xs: 3.5, md: 4 },
          backgroundColor: 'greens.lightest',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: 'left',
          gap: { xs: 2, md: 0 },
        }}
      >
        {icon ? (
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              alignSelf: { xs: 'center', md: 'start' },
              marginRight: { xs: 0, md: 2 },
            }}
          >
            <Box
              sx={{
                bgcolor: 'greens.dark',
                borderRadius: '50%',
                height: '60px',
                width: '60px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <BrandIcon
                name={icon}
                size={40}
                svgStyles={{
                  filter: 'brightness(0) invert(1)',
                }}
              />
            </Box>
          </Box>
        ) : null}
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 3 }}>
          <Box sx={{ display: 'flex' }}>
            <Typography variant="textBold">{headline}</Typography>
          </Box>
          <Box sx={{ display: 'flex' }}>
            <Typography variant="text">{subText}</Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          padding: { xs: 3.5, md: 4 },
          marginBottom: 1,
        }}
      >
        {answers?.length > 0 && (
          <select
            aria-label="Select an answer"
            id="answer"
            name="answer"
            value={selectedAnswer?.answer || ''}
            onChange={e => {
              const currentAnswer = answers.find(a => a.answer === e.target.value);
              if (currentAnswer) {
                setSelectedAnswer(currentAnswer);
              }
            }}
            style={{
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none',
              height: '44px',
              border: `1px solid ${theme.palette.neutral.light}`,
              backgroundColor: `${theme.palette.common.white}`,
              paddingTop: '8px',
              paddingBottom: '8px',
              paddingLeft: '16px',
              paddingRight: '40px',
              boxSizing: 'border-box',
              borderRadius: '4px',
              outline: 'none',
              width: '100%',
              maxWidth: '100%',
              backgroundImage: `url("${encodedArrow}")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 16px center',
              cursor: 'pointer',
              fontFamily: 'inherit',
              color: theme.palette.text.primary,
              minWidth: '136px',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {answers.map(option => (
              <option key={option.answer} value={option.answer}>
                {option.answer}
              </option>
            ))}
          </select>
        )}
        <Box sx={{ marginTop: 2, display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
          <Button
            href={selectedAnswer?.destination || 'https://www.nerdwallet.com'}
            target="_blank"
          >
            {ctaText}
          </Button>
          <Typography
            variant="textSmall"
            sx={{
              color: 'neutral.darker',
              marginTop: 1,
            }}
          >
            {"on NerdWallet's secure site"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
